import { Box, CircularProgress, Dialog, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import COLORS from "../../../../constants/Colors";
import NearMeRoundedIcon from "@mui/icons-material/NearMeRounded";
import { useDashLayoutCtx } from "../DashLayoutCtxProvider";
import { useNavigate } from "react-router-dom";
import useNetworkRequest from "../../../../hooks/NetworkRequest";
import { ENDPOINTS, urlWithPrefix } from "../../../../configs/APIConfig";
import notify from "../../../../funcs/ToastFuncs";
import WheelzTextInput from "../../../common/inputs/WheelzTextInput";
import APP_CONSTANTS from "../../../../constants/AppConstants";
import STRINGS from "../../../../assets/strings/Strings";
import WheelzButton, {
  WHEELZ_BUTTON_TYPES,
} from "../../../common/buttons/WheelzButton";

function TrackingAlertBanner({
  isEnabled = true,
  vehRegNo = null,
  ownerName = "",
  manufName = "",
}) {
  const { setsplitView, setpreloadVehTracking } = useDashLayoutCtx();
  const navigate = useNavigate();
  const { fetchData } = useNetworkRequest();
  const abortControllerRef = useRef(null);
  const [isLoading, setisLoading] = useState(false);
  const [trackingAppoved, settrackingAppoved] = useState(false);

  const navToErrorScreen = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    navigate("/error", {
      state: {
        releaseProtection: true,
      },
    });
  };

  const openTracking = () => {
    setsplitView(false);
    setpreloadVehTracking({
      vehRegNo: vehRegNo,
    });
    navigate("/dashboard/tracking");
  };

  const createTrackingRequest = async (ownerNum) => {
    setisLoading(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let payload = {
      vehicle_number: vehRegNo,
      owner_number: ownerNum,
    };

    try {
      const resp = await fetchData({
        url: urlWithPrefix(ENDPOINTS.DASHBOARD.TRACKING.CREATE_REQUEST.url),
        method: ENDPOINTS.DASHBOARD.TRACKING.CREATE_REQUEST.method,
        authRequired: true,
        timeout: 20000, // 20 Sec timeout
        signal: abortController.signal,
        body: payload,
      });
      if (resp.status === 200) {
        settrackingAppoved(true);
        notify("Tracking request approved.", "success");
        setTimeout(() => {
          notify("Saved " + vehRegNo + " to your tracking list.", "success");
          setTimeout(() => {
            openTracking();
          }, 1000); // 1-second delay
        }, 1000); // 1-second delay
      } else {
        notify("Failed to create tracking request.", "error");
      }
    } catch (error) {
      const statusCode = error.response.status;
      const errorCode = error.response.data.error.code;
      if (statusCode === 400 && errorCode === "tracking-limit-reached") {
        notify("Tracking limit reached.", "error");
      } else if (error?.code !== "ERR_CANCELED") {
        console.error("Failed to create tracking request.");
        navToErrorScreen();
      }
    }
    setisLoading(false);
  };

  const [openReqTrackDialog, setopenReqTrackDialog] = useState(false);

  const ReqTrackDialog = () => {
    const [inputPhoneNo, setinputPhoneNo] = useState("");
    const [inputPhoneNoInvalid, setinputPhoneNoInvalid] = useState(false);
    const [inputPhoneNoErrMsg, setinputPhoneNoErrMsg] = useState("");
    const [buttonLoading, setbuttonLoading] = useState(false);
    const [disableReqBtn, setdisableReqBtn] = useState(false);
    useEffect(() => {
      setinputPhoneNo("");
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      if (inputPhoneNo.length === APP_CONSTANTS.MAX_PHONE_NO_LENGTH) {
        setdisableReqBtn(false);
      } else {
        setdisableReqBtn(true);
      }
    }, [inputPhoneNo]);

    const validatePhoneNumber = () => {
      // // Regex pattern for 10 digits
      const phoneRegex = /^[6789]\d{9}$/;
      if (!phoneRegex.test(inputPhoneNo)) {
        setinputPhoneNoInvalid(true);
        setinputPhoneNoErrMsg(STRINGS.LOGIN_SCREEN.INVALID_PHONE_NO);
        return false;
      } else {
        resetPhoneNoInputErr();
        return true;
      }
    };
    const handleLogin = () => {
      if (validatePhoneNumber()) {
        setbuttonLoading(true);
        createTrackingRequest(inputPhoneNo);
        setopenReqTrackDialog(false);
      }
    };
    const resetPhoneNoInputErr = () => {
      setinputPhoneNoErrMsg("");
      setinputPhoneNoInvalid(false);
    };
    const handlePhoneNoInputChange = (event) => {
      resetPhoneNoInputErr();
      let inputValue = event.target.value;
      if (inputValue.length > APP_CONSTANTS.MAX_PHONE_NO_LENGTH) {
        inputValue = inputValue.slice(0, APP_CONSTANTS.MAX_PHONE_NO_LENGTH);
      }
      setinputPhoneNo(inputValue);
    };

    return (
      <Dialog
        open={openReqTrackDialog}
        onClose={() => setopenReqTrackDialog(false)}
      >
        <Box
          sx={{
            width: "500px",
            margin: "40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography fontSize={"1.2rem"} fontWeight={600}>
              Request FASTag tracking.
            </Typography>
            <Typography
              fontSize={"0.8rem"}
              fontWeight={600}
              color={COLORS.PRIMARY}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => setopenReqTrackDialog(false)}
            >
              CLOSE
            </Typography>
          </Box>

          <Typography
            fontSize={"0.9rem"}
            fontWeight={400}
            sx={{
              mt: "20px",
              opacity: "0.6",
            }}
          >
            Provide the registered FASTag owner's mobile number as mandated by
            ULIP to ensure authenticity.
          </Typography>
          <Box
            sx={{
              width: "100%",
              mt: "40px",
              mb: "20px",
            }}
          >
            <WheelzTextInput
              label={"Owner mobile number"}
              placeholder={"Enter mobile no here"}
              numberInput
              phoneNoInput
              primaryColorLabel
              value={inputPhoneNo}
              onChange={handlePhoneNoInputChange}
              showAdornment={inputPhoneNo.length !== 0}
              error={inputPhoneNoInvalid}
              helperText={inputPhoneNoInvalid && inputPhoneNoErrMsg}
              onEnterFunc={handleLogin}
            />
          </Box>
          <WheelzButton
            buttonText={"Request"}
            width={"100%"}
            deCapitalizeText
            buttonType={WHEELZ_BUTTON_TYPES.PRIMARY}
            onClick={buttonLoading ? () => {} : handleLogin}
            disabled={disableReqBtn}
            loading={buttonLoading}
          />
        </Box>
      </Dialog>
    );
  };

  const openReqDialog = () => {
    setopenReqTrackDialog(true);
  };

  return (
    <Box
      sx={{
        width: "calc(100% - 48px)",
        background: isEnabled ? "var(--input-field-active)" : COLORS.DASH_BG,
        m: "40px 24px 10px 24px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        borderRadius: "4px",
        alignItems: "center",
      }}
    >
      <ReqTrackDialog />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            height: "50px",
            width: "50px",
            background: isEnabled ? COLORS.TRACKING_GREEN : COLORS.PRIMARY_GRAD,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "4px",
          }}
        >
          <NearMeRoundedIcon sx={{ fontSize: "1.6rem", color: "white" }} />
        </Box>
        <Typography
          fontSize={"0.9rem"}
          fontWeight={600}
          sx={{
            ml: "20px",
            opacity: "0.8",
          }}
        >
          {isEnabled
            ? "FASTag tracking is enabled."
            : "Request FASTag tracking."}
        </Typography>
      </Box>
      {!isLoading && (
        <Typography
          fontSize={"0.9rem"}
          fontWeight={600}
          color={COLORS.PRIMARY}
          sx={{
            marginRight: "20px",
            cursor: "pointer",
          }}
          onClick={
            isEnabled
              ? openTracking
              : trackingAppoved
              ? () => {}
              : openReqDialog // open dialog then createTrackingRequest
          }
        >
          {isEnabled ? "View" : trackingAppoved ? "Approved" : "Request"}
        </Typography>
      )}
      {isLoading && (
        <CircularProgress
          size={20}
          sx={{
            mr: "20px",
          }}
          style={{ color: COLORS.PRIMARY }}
        />
      )}
    </Box>
  );
}

export default TrackingAlertBanner;
